import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from "axios";
import CryptoJS from "crypto-js";

interface SecurityIncident {
  type:
  | "authentication_failure"
  | "authorization_failure"
  | "suspicious_activity"
  | "data_breach"
  | "system_compromise";
  severity: "low" | "medium" | "high" | "critical";
  details: any;
  timestamp: Date;
  userAgent?: string;
  ipAddress?: string;
}

class ApiClient {
  private client: AxiosInstance;
  private encryptionKey: string;
  private securityIncidents: SecurityIncident[] = [];

  constructor() {
    // Generate or retrieve encryption key for sensitive data
    this.encryptionKey = this.getOrCreateEncryptionKey();

    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || "http://localhost:3001",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
        "X-Client-Version": "1.0.0",
        "X-Security-Protocol": "TLS-1.3",
      },
      // Enable HTTPS only in production
      httpsAgent: import.meta.env.PROD
        ? {
          rejectUnauthorized: true,
          secureProtocol: "TLSv1_3_method",
        }
        : undefined,
    });

    // Request interceptor with security enhancements
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem("auth_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add request ID for audit trail
        config.headers["X-Request-ID"] = this.generateRequestId();

        // Add timestamp for replay attack prevention
        config.headers["X-Timestamp"] = Date.now().toString();

        // Encrypt sensitive data in request body
        if (config.data && this.containsSensitiveData(config.data)) {
          config.data = this.encryptSensitiveData(config.data);
          config.headers["X-Data-Encrypted"] = "true";
        }

        return config;
      },
      (error) => {
        this.logSecurityIncident({
          type: "system_compromise",
          severity: "medium",
          details: { error: error.message, phase: "request_preparation" },
          timestamp: new Date(),
        });
        return Promise.reject(error);
      }
    );

    // Response interceptor with comprehensive security error handling
    this.client.interceptors.response.use(
      (response) => {
        // Decrypt response data if encrypted
        if (response.headers["x-data-encrypted"] === "true" && response.data) {
          response.data = this.decryptSensitiveData(response.data);
        }

        return response;
      },
      (error: AxiosError) => {
        this.handleSecurityError(error);
        return Promise.reject(error);
      }
    );
  }

  private getOrCreateEncryptionKey(): string {
    let key = sessionStorage.getItem("client_encryption_key");
    if (!key) {
      key = CryptoJS.lib.WordArray.random(256 / 8).toString();
      sessionStorage.setItem("client_encryption_key", key);
    }
    return key;
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private containsSensitiveData(data: any): boolean {
    const sensitiveFields = [
      "password",
      "token",
      "secret",
      "key",
      "credential",
      "ssn",
      "credit_card",
    ];
    const dataStr = JSON.stringify(data).toLowerCase();
    return sensitiveFields.some((field) => dataStr.includes(field));
  }

  private encryptSensitiveData(data: any): any {
    try {
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), this.encryptionKey).toString();
      return { encrypted_payload: encrypted };
    } catch (error) {
      console.error("Encryption error:", error);
      return data; // Fallback to unencrypted if encryption fails
    }
  }

  private decryptSensitiveData(data: any): any {
    try {
      if (data.encrypted_payload) {
        const decrypted = CryptoJS.AES.decrypt(data.encrypted_payload, this.encryptionKey);
        return JSON.parse(decrypted.toString(CryptoJS.enc.Utf8));
      }
      return data;
    } catch (error) {
      console.error("Decryption error:", error);
      return data; // Fallback to original data if decryption fails
    }
  }

  private handleSecurityError(error: AxiosError): void {
    const status = error.response?.status;
    const config = error.config;

    switch (status) {
      case 401:
        this.logSecurityIncident({
          type: "authentication_failure",
          severity: "high",
          details: {
            url: config?.url,
            method: config?.method,
            hasToken: !!localStorage.getItem("auth_token"),
          },
          timestamp: new Date(),
        });
        this.handleAuthenticationFailure();
        break;

      case 403:
        this.logSecurityIncident({
          type: "authorization_failure",
          severity: "medium",
          details: {
            url: config?.url,
            method: config?.method,
            response: error.response?.data,
          },
          timestamp: new Date(),
        });
        break;

      case 429:
        this.logSecurityIncident({
          type: "suspicious_activity",
          severity: "medium",
          details: {
            type: "rate_limit_exceeded",
            url: config?.url,
            retryAfter: error.response?.headers["retry-after"],
          },
          timestamp: new Date(),
        });
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        this.logSecurityIncident({
          type: "system_compromise",
          severity: "high",
          details: {
            status,
            url: config?.url,
            error: error.message,
          },
          timestamp: new Date(),
        });
        break;

      default:
        if (status && status >= 400) {
          this.logSecurityIncident({
            type: "suspicious_activity",
            severity: "low",
            details: {
              status,
              url: config?.url,
              error: error.message,
            },
            timestamp: new Date(),
          });
        }
    }
  }

  private logSecurityIncident(incident: SecurityIncident): void {
    // Add browser/environment info
    incident.userAgent = navigator.userAgent;

    // Store locally for immediate access
    this.securityIncidents.push(incident);

    // Keep only last 100 incidents to prevent memory issues
    if (this.securityIncidents.length > 100) {
      this.securityIncidents = this.securityIncidents.slice(-100);
    }

    // Send to security monitoring service (fire and forget)
    this.reportSecurityIncident(incident).catch((error) => {
      console.error("Failed to report security incident:", error);
    });
  }

  private async reportSecurityIncident(incident: SecurityIncident): Promise<void> {
    try {
      // Use a separate axios instance to avoid interceptor loops
      const securityClient = axios.create({
        baseURL: this.client.defaults.baseURL,
        timeout: 5000,
        headers: {
          "Content-Type": "application/json",
          "X-Security-Report": "true",
        },
      });

      await securityClient.post("/api/security/incidents", incident);
    } catch (error) {
      // Silently fail - don't create more incidents from incident reporting
      console.warn("Security incident reporting failed:", error);
    }
  }

  private handleAuthenticationFailure(): void {
    // Clear all auth-related data
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("client_encryption_key");

    // Clear any cached user data
    localStorage.removeItem("user_profile");
    localStorage.removeItem("user_permissions");

    // Redirect to login with security context
    const currentPath = window.location.pathname;
    const securityContext = btoa(
      JSON.stringify({
        reason: "authentication_failure",
        timestamp: Date.now(),
        path: currentPath,
      })
    );

    window.location.href = `/login?security=${securityContext}`;
  }

  // Public method to get security incidents for dashboard
  public getSecurityIncidents(): SecurityIncident[] {
    return [...this.securityIncidents];
  }

  // Public method to clear security incidents
  public clearSecurityIncidents(): void {
    this.securityIncidents = [];
  }

  async get<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config);
  }

  async put<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async delete<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  // Security Services API Methods

  // Security Monitoring
  async startSecurityMonitoring(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.post(`/api/security/monitoring/${deploymentId}/start`);
  }

  async stopSecurityMonitoring(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.post(`/api/security/monitoring/${deploymentId}/stop`);
  }

  async getSecurityPosture(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/security/posture/${deploymentId}`);
  }

  async performSecurityScan(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.post(`/api/security/scan/${deploymentId}`);
  }

  async updateThreatStatus(
    threatId: string,
    status: string,
    notes?: string
  ): Promise<AxiosResponse<any>> {
    return this.patch(`/api/security/threats/${threatId}`, { status, notes });
  }

  // Compliance Services
  async generateComplianceReport(params: {
    deploymentId: string;
    framework: "SOC2" | "ISO27001" | "CIS";
    startDate: string;
    endDate: string;
    includeEvidence?: boolean;
    reportFormat?: "json" | "pdf" | "html";
  }): Promise<AxiosResponse<any>> {
    return this.post("/api/security/compliance/report", params);
  }

  async getComplianceMetrics(
    deploymentId: string,
    framework?: string
  ): Promise<AxiosResponse<any>> {
    const params = framework ? { framework } : {};
    return this.get(`/api/security/compliance/metrics/${deploymentId}`, { params });
  }

  async getComplianceTrends(
    deploymentId: string,
    framework: string,
    days?: number
  ): Promise<AxiosResponse<any>> {
    const params = { framework, days };
    return this.get(`/api/security/compliance/trends/${deploymentId}`, { params });
  }

  // Audit Services
  async getAuditTrail(
    deploymentId: string,
    startDate?: string,
    endDate?: string
  ): Promise<AxiosResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return this.get(`/api/security/audit/${deploymentId}`, { params });
  }

  async validateAuditIntegrity(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.post(`/api/security/audit/validate/${deploymentId}`);
  }

  async exportAuditData(
    deploymentId: string,
    format: "json" | "csv" | "pdf",
    startDate?: string,
    endDate?: string
  ): Promise<AxiosResponse<any>> {
    return this.post(`/api/security/audit/export/${deploymentId}`, {
      format,
      startDate,
      endDate,
    });
  }

  // Dashboard Services
  async getDashboardOverview(): Promise<AxiosResponse<any>> {
    return this.get("/api/dashboard/overview");
  }

  async getDeployments(): Promise<AxiosResponse<any>> {
    return this.get("/api/dashboard/deployments");
  }

  async getRecentDeployments(): Promise<AxiosResponse<any>> {
    return this.get("/api/dashboard/deployments/recent");
  }

  async getDeploymentById(id: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/dashboard/deployments/${id}`);
  }

  async getCostAnalytics(): Promise<AxiosResponse<any>> {
    return this.get("/api/dashboard/costs");
  }

  // Deployment Services
  async createDeployment(deploymentConfig: any): Promise<AxiosResponse<any>> {
    return this.post("/api/deploy", deploymentConfig);
  }

  async getDeploymentStatus(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/deploy/${deploymentId}/status`);
  }

  async getDeploymentLogs(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/deploy/${deploymentId}/logs`);
  }

  async cancelDeployment(deploymentId: string): Promise<AxiosResponse<any>> {
    return this.post(`/api/deploy/${deploymentId}/cancel`);
  }

  // Rule Engine Services
  async analyzeRequirements(requirements: any): Promise<AxiosResponse<any>> {
    return this.post("/api/rules/analyze", requirements);
  }

  async getCompatibilityMatrix(version: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/rules/compatibility/${version}`);
  }

  async validateConfiguration(config: any): Promise<AxiosResponse<any>> {
    return this.post("/api/rules/validate", config);
  }

  // Module Services
  async getModules(version?: string): Promise<AxiosResponse<any>> {
    const params = version ? { version } : {};
    return this.get("/api/modules", { params });
  }

  async getModuleDetails(moduleName: string, version?: string): Promise<AxiosResponse<any>> {
    const params = version ? { version } : {};
    return this.get(`/api/modules/${moduleName}`, { params });
  }

  // Recommendation Services
  async createRecommendation(requirements: any): Promise<AxiosResponse<any>> {
    return this.post("/api/recommendations", requirements);
  }

  async getRecommendation(sessionId: string): Promise<AxiosResponse<any>> {
    return this.get(`/api/recommendations/${sessionId}`);
  }

  async updateRecommendation(sessionId: string, data: any): Promise<AxiosResponse<any>> {
    return this.put(`/api/recommendations/${sessionId}`, data);
  }

  async validateModules(sessionId: string, selectedModules: string[]): Promise<AxiosResponse<any>> {
    return this.post("/api/validate", { sessionId, modules: selectedModules });
  }

  async orchestrateDeployment(sessionId: string, data: any): Promise<AxiosResponse<any>> {
    return this.post("/api/orchestrate", { sessionId, ...data });
  }

  // Health Check Services
  async getSystemHealth(): Promise<AxiosResponse<any>> {
    return this.get("/health");
  }

  async getDetailedHealth(): Promise<AxiosResponse<any>> {
    return this.get("/health/detailed");
  }

  // Authentication Services
  async login(credentials: { email: string; password: string }): Promise<AxiosResponse<any>> {
    return this.post("/api/auth/login", credentials);
  }

  async logout(): Promise<AxiosResponse<any>> {
    return this.post("/api/auth/logout");
  }

  async refreshToken(): Promise<AxiosResponse<any>> {
    return this.post("/api/auth/refresh");
  }

  async getCurrentUser(): Promise<AxiosResponse<any>> {
    return this.get("/api/auth/me");
  }
}

export const apiClient = new ApiClient();
